import React from 'react';
import { motion } from 'framer-motion';
import { FlameIcon, CheckCircleIcon } from 'lucide-react';
import { PhoneMockup } from './PhoneMockup';
export function HeroSection() {
  return <section className="relative min-h-screen pt-24 pb-16 flex items-center overflow-hidden" data-id="element-102">
      {/* Background subtle gradient */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-ascension-green/10 rounded-full blur-[120px] pointer-events-none" data-id="element-103" />

      <div className="max-w-7xl mx-auto px-6 w-full grid grid-cols-1 md:grid-cols-2 gap-16 items-center z-10" data-id="element-104">
        {/* Left Content */}
        <motion.div initial={{
        opacity: 0,
        x: -30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} className="flex flex-col gap-6" data-id="element-105">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] tracking-tight" data-id="element-106">
            Build Discipline. <br data-id="element-107" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-ascension-gray" data-id="element-108">
              One Day at a Time.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-ascension-gray max-w-lg leading-relaxed" data-id="element-109">
            Ascension is a habit-building system that holds you accountable by
            locking distractions until you complete your task.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mt-4" data-id="element-110">
            <button className="bg-ascension-green hover:bg-[#3bb34c] text-black px-8 py-4 rounded-full font-semibold text-lg transition-all duration-200 shadow-[0_0_20px_rgba(68,198,87,0.3)] hover:shadow-[0_0_30px_rgba(68,198,87,0.5)]" data-id="element-111">
              Join Early Access
            </button>
            <button className="border border-white/20 hover:bg-white/5 text-white px-8 py-4 rounded-full font-medium text-lg transition-colors duration-200" data-id="element-112">
              See How It Works
            </button>
          </div>
        </motion.div>

        {/* Right Content - Phone Mockup */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.2,
        ease: 'easeOut'
      }} className="flex justify-center md:justify-end relative" data-id="element-113">
          <PhoneMockup glowColor="green" floating={true} data-id="element-114">
            {/* Phone UI Inner Content */}
            <div className="flex flex-col h-full gap-6 mt-4" data-id="element-115">
              {/* Header */}
              <div className="flex justify-between items-center" data-id="element-116">
                <div className="flex items-center gap-2 text-ascension-green font-medium" data-id="element-117">
                  <FlameIcon size={18} className="fill-ascension-green" data-id="element-118" />
                  <span data-id="element-119">14 Day Streak</span>
                </div>
                <div className="w-8 h-8 rounded-full bg-ascension-dark border border-white/10" data-id="element-120" />
              </div>

              {/* Weekly Progress Ring */}
              <div className="flex justify-center my-4 relative" data-id="element-121">
                <svg className="w-40 h-40 transform -rotate-90" data-id="element-122">
                  <circle cx="80" cy="80" r="70" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-ascension-dark" data-id="element-123" />
                  <circle cx="80" cy="80" r="70" stroke="currentColor" strokeWidth="8" fill="transparent" strokeDasharray="440" strokeDashoffset="110" className="text-ascension-green drop-shadow-[0_0_8px_rgba(68,198,87,0.5)]" strokeLinecap="round" data-id="element-124" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-125">
                  <span className="text-3xl font-bold text-white" data-id="element-126">75%</span>
                  <span className="text-xs text-ascension-gray" data-id="element-127">
                    Weekly Goal
                  </span>
                </div>
              </div>

              {/* Today's Task */}
              <div className="mt-auto" data-id="element-128">
                <h3 className="text-sm font-medium text-ascension-gray mb-3" data-id="element-129">
                  Today's Protocol
                </h3>
                <div className="bg-ascension-dark rounded-2xl p-4 border border-white/5 relative overflow-hidden" data-id="element-130">
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-ascension-red" data-id="element-131" />
                  <div className="flex items-start justify-between" data-id="element-132">
                    <div data-id="element-133">
                      <h4 className="text-white font-medium mb-1" data-id="element-134">
                        Deep Work Block
                      </h4>
                      <p className="text-xs text-ascension-gray" data-id="element-135">
                        90 minutes focused coding
                      </p>
                    </div>
                    <div className="w-6 h-6 rounded-full border-2 border-white/20 flex items-center justify-center" data-id="element-136">
                      <div className="w-2 h-2 rounded-full bg-transparent" data-id="element-137" />
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between" data-id="element-138">
                    <span className="text-xs text-ascension-red font-medium flex items-center gap-1" data-id="element-139">
                      <LockIcon size={12} data-id="element-140" /> Apps Locked
                    </span>
                    <button className="text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg transition-colors" data-id="element-141">
                      Begin
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </PhoneMockup>
        </motion.div>
      </div>
    </section>;
}
// Inline LockIcon for the mockup
function LockIcon({
  size = 24,
  className = ''
}: {
  size?: number;
  className?: string;
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-142">
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" data-id="element-143" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" data-id="element-144" />
    </svg>;
}